import { configureFrameworkTranslation } from "zudoku/hooks";
import { getFrameworkTranslator } from "./lib/framework-localization";
import { DocsNavigation } from "./components/docs-navigation";
import type { ZudokuConfig } from "zudoku";
import { Callout } from "zudoku/ui/Callout";
import { Card, CardDescription, CardHeader, CardTitle } from "zudoku/ui/Card";
import { Stepper } from "zudoku/ui/Stepper";
import { DocImage } from "./components/doc-image";
import { DocsHeaderActions } from "./components/docs-header-actions";
import { DocsRuntime } from "./components/docs-runtime";
import { IntercomContactLink } from "./components/intercom-contact-link";
import {
  MultilingualLanguageCatalog,
  SpeakerLanguageCatalog,
  TargetLanguageCatalog,
} from "./components/language-catalog";
import { PageShareButtons } from "./components/page-share-buttons";
import {
  DOCS_LOCALES,
  DOCS_ROUTE_SUFFIXES,
  LEGACY_DOCS_ROUTE_REDIRECTS,
  getLocalizedDocsPath,
} from "./lib/docs-routes";
import { APP_ORIGIN, DOCS_ORIGIN, getDocsMessages } from "./lib/localization";

configureFrameworkTranslation(getFrameworkTranslator);

const brandAccent = "#3567e6";
const appSansFont =
  "ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";
const appMonoFont =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const documentationNavigation: NonNullable<ZudokuConfig["navigation"]> =
  DOCS_LOCALES.map((locale) => {
    const messages = getDocsMessages(locale);

    return {
      type: "category",
      label: messages.navigationDocumentation,
      display: "hide",
      items: [
        {
          type: "doc",
          label: messages.navigationWelcome,
          icon: "house",
          file: `${locale}/welcome`,
        },
        {
          type: "category",
          label: messages.navigationGettingStarted,
          icon: "rocket",
          collapsed: false,
          link: {
            type: "doc",
            file: `${locale}/getting-started`,
            label: messages.navigationGettingStarted,
          },
          items: [
            `${locale}/getting-started/set-up-your-account`,
            `${locale}/getting-started/set-up-your-audio-input`,
            `${locale}/getting-started/start-translating`,
            `${locale}/getting-started/share-with-your-church`,
          ],
        },
        {
          type: "doc",
          label: messages.navigationSupportedLanguages,
          icon: "languages",
          file: `${locale}/supported-languages`,
        },
        {
          type: "category",
          label: messages.navigationHowTo,
          icon: "wrench",
          collapsed: true,
          items: [
            `${locale}/how-to/customize-the-translation`,
            `${locale}/how-to/download-past-translations`,
          ],
        },
        {
          type: "category",
          label: messages.navigationIntegrations,
          icon: "plug",
          items: [
            `${locale}/integrations/mcp-server`,
            `${locale}/integrations/api`,
          ],
        },
        {
          type: "link",
          label: messages.dashboard,
          to: APP_ORIGIN,
          target: "_blank",
        },
      ],
    };
  });

const apiNavigationLabel = (locale: (typeof DOCS_LOCALES)[number]) =>
  `${getDocsMessages(locale).navigationApiReference} (${locale})`;

const navigation: NonNullable<ZudokuConfig["navigation"]> = [
  ...documentationNavigation,
  ...DOCS_LOCALES.map((locale) => ({
    type: "link" as const,
    label: apiNavigationLabel(locale),
    to: `/${locale}/api`,
    display: "hide" as const,
  })),
];

const config: ZudokuConfig = {
  site: {
    title: "Kaleo AI",
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.png", dark: "/logo-dark.png" },
      alt: "Kaleo AI",
      width: "130px",
      href: "/",
      reloadDocument: true,
    },
  },
  metadata: {
    title: "%s - Kaleo AI",
    defaultTitle: "Kaleo AI",
    favicon: "/favicon.ico",
  },
  header: {
    placements: {
      search: "center",
    },
  },
  apis: DOCS_LOCALES.map((locale) => ({
    type: "file",
    input:
      locale === "en" ? "./openapi/kaleo-api.json" : `./openapi/${locale}.json`,
    path: `/${locale}/api`,
    options: {
      showInfoPage: true,
      expandAllTags: true,
      disableSecurity: false,
      disablePlayground: false,
      examplesLanguage: "shell",
      supportedLanguages: [
        { value: "shell", label: "cURL" },
        { value: "javascript", label: "JavaScript" },
      ],
      schemaDownload: { enabled: true, fileName: "kaleo-api" },
    },
  })),
  navigation,
  navigationRules: DOCS_LOCALES.flatMap((locale) => {
    const messages = getDocsMessages(locale);
    return [
      {
        type: "modify" as const,
        match: `${apiNavigationLabel(locale)}/Information`,
        set: { label: messages.navigationGettingStarted },
      },
      {
        type: "modify" as const,
        match: `${apiNavigationLabel(locale)}/Schemas`,
        set: { label: messages.navigationSchemas },
      },
      {
        type: "insert" as const,
        match: `${apiNavigationLabel(locale)}/1/2`,
        position: "after" as const,
        items: [
          {
            type: "doc" as const,
            file: `${locale}/api/languages/special-language-values`,
          },
        ],
      },
    ];
  }),
  redirects: [
    { from: "/", to: getLocalizedDocsPath("en") },
    { from: "/en/integrations/kaleo-api", to: "/en/integrations/api" },
    ...Object.entries(LEGACY_DOCS_ROUTE_REDIRECTS).map(([from, to]) => ({
      from: `/${from}`,
      to: getLocalizedDocsPath("en", to),
    })),
    ...DOCS_ROUTE_SUFFIXES.map((suffix) => ({
      from: `/${suffix}`,
      to: getLocalizedDocsPath("en", suffix),
    })),
  ],
  slots: {
    "top-navigation-before": <DocsNavigation />,
    "head-navigation-end": <DocsHeaderActions />,
    "layout-after-head": (
      <>
        <DocsRuntime />
        <DocsNavigation mobile />
      </>
    ),
    "mobile-top-bar-end": <DocsHeaderActions compact />,
  },
  mdx: {
    components: {
      Callout,
      Card,
      CardDescription,
      CardHeader,
      CardTitle,
      DocImage,
      IntercomContactLink,
      MultilingualLanguageCatalog,
      PageShareButtons,
      SpeakerLanguageCatalog,
      Stepper,
      TargetLanguageCatalog,
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
  },
  docs: {
    publishMarkdown: true,
    defaultOptions: {
      showLastModified: false,
    },
  },
  sitemap: {
    siteUrl: DOCS_ORIGIN,
  },
  theme: {
    fonts: {
      sans: { url: "", fontFamily: appSansFont },
      mono: { url: "", fontFamily: appMonoFont },
    },
    light: {
      primary: brandAccent,
      primaryForeground: "#ffffff",
      ring: brandAccent,
    },
    dark: {
      primary: brandAccent,
      primaryForeground: "#ffffff",
      ring: brandAccent,
    },
    customCss: `
      .kaleo-docs-navigation-mobile { display: none !important; }
      @media (max-width: 63.999rem) { .kaleo-docs-navigation-mobile { display: flex !important; } }
      :root:has(.kaleo-docs-navigation) { --top-nav-height: 3rem; }
      .kaleo-docs-navigation { display: flex; gap: 2rem; padding: 0 2rem; height: 3rem; align-items: center; font-size: .875rem; }
      .kaleo-docs-navigation a { height: 100%; display: flex; align-items: center; font-weight: 500; border-bottom: 2px solid transparent; }
      .kaleo-docs-navigation a[aria-current="page"] { border-color: var(--primary); }

      :root {
        --callout-note: lab(47.6397% 1.44139 -7.37348);
        --callout-info: lab(50.4177% -3.27396 -53.6094);
        --callout-tip: lab(57.9394% -47.8174 40.6994);
        --callout-danger: lab(51.3582% 69.7345 44.3509);
        --callout-caution: lab(64.9778% 22.5509 80.4318);
        --callout-sparkles: lab(50.3845% 45.1614 -61.7514);
        --callout-rocket: lab(45.213% 24.3682 -70.9041);
        --callout-settings: lab(41.8672% 1.47718 -5.43494);
        --callout-zap: lab(63.3292% 4.71273 101.951);
        --callout-lock: lab(44.49% -11.3478 -16.9659);
        --callout-megaphone: lab(56.586% -32.1465 -34.9248);
      }

      .dark {
        --callout-note: lab(67.3627% 1.39615 -7.39749);
        --callout-info: lab(66.8198% -6.42365 -53.9665);
        --callout-tip: lab(73.0584% -47.6983 39.599);
        --callout-danger: lab(63.2066% 63.0145 37.4065);
        --callout-caution: lab(76.6366% 21.5807 73.1029);
        --callout-sparkles: lab(64.8937% 38.1655 -55.9412);
        --callout-rocket: lab(63.3345% 15.3298 -64.2612);
        --callout-settings: lab(67.3894% 1.44428 -5.45396);
        --callout-zap: lab(82.9802% 7.23806 73.6904);
        --callout-lock: lab(65.3887% -11.769 -17.1016);
        --callout-megaphone: lab(71.6811% -31.5902 -32.9072);
      }

      [data-kaleo-framework="page-actions"] {
        position: relative;
        z-index: 1;
      }

      /* Zudoku 0.82 has no separate Information-page section toggles. */
      [data-pagefind-filter="section:openapi"] div:has(> div.uppercase > svg:is(.lucide-tag, .lucide-lock)) {
        display: none;
      }

      header [data-kaleo-framework="theme-toggle"] {
        order: -1;
      }

      header [data-slot="navigation-menu-link"][href^="https://app.withkaleo.com"] {
        height: 2rem;
        padding-inline: 0.875rem;
        border-radius: 0.5rem;
        color: var(--primary-foreground);
        background-color: var(--primary);
      }

      header [data-slot="navigation-menu-link"][href^="https://app.withkaleo.com"]:hover,
      header [data-slot="navigation-menu-link"][href^="https://app.withkaleo.com"]:focus-visible {
        color: var(--primary-foreground);
        background-color: color-mix(in srgb, var(--primary) 88%, black);
      }

      .typography > header > [data-pagefind-ignore="all"] {
        margin-bottom: 0.75rem;
      }

      a[aria-current="page"].bg-accent {
        color: var(--primary);
        background-color: color-mix(in srgb, var(--primary) 14%, transparent);
      }

      a[aria-current="page"]:has(+ [data-state] [aria-current="page"]) > div {
        color: color-mix(in srgb, var(--foreground) 80%, transparent);
      }

      .typography [role="note"].not-prose {
        grid-template-columns: min-content minmax(0, 1fr);
        grid-template-rows: auto auto;
        align-items: start;
        column-gap: 0.75rem;
        row-gap: 0.25rem;
        margin-block: 0.75rem;
        padding: 0.75rem 1rem;
        border-radius: 0.75rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .typography [role="note"].not-prose > svg {
        width: 1.125rem;
        height: 1.125rem;
        margin-top: 0.125rem;
        flex-shrink: 0;
        transform: none;
        translate: none !important;
      }

      .typography [role="note"].not-prose > p,
      .typography [role="note"].not-prose > div {
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .typography [role="note"].not-prose > div > :first-child {
        margin-top: 0;
      }

      .typography [role="note"].not-prose > div > :last-child {
        margin-bottom: 0;
      }

      .typography [role="note"].not-prose > div:last-child {
        color: color-mix(in srgb, var(--foreground) 90%, transparent);
      }

      .typography [role="note"].not-prose:not(:has(> p)) {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
      }

      .typography [role="note"].not-prose:not(:has(> p)) > div {
        display: flex;
        min-width: 0;
        flex: 1 1 0%;
        flex-direction: column;
        grid-column: auto;
        gap: 0.25rem;
      }

      .kaleo-sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .kaleo-intercom-trigger {
        padding: 0;
        border: 0;
        color: var(--primary);
        background: transparent;
        font: inherit;
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 3px;
        cursor: pointer;
      }

      .kaleo-intercom-trigger:hover {
        text-decoration-thickness: 2px;
      }

      .typography strong .kaleo-intercom-trigger {
        font-weight: 700;
      }

      .kaleo-docs-header-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .kaleo-locale-selector {
        --kaleo-locale-chevron-gap: 0.375rem;
        --kaleo-locale-chevron-inset: 0.875rem;
        --kaleo-locale-chevron-size: 1rem;
        position: relative;
        display: inline-flex;
      }

      .kaleo-locale-selector select {
        max-width: 11.5rem;
        height: 2rem;
        padding: 0
          calc(
            var(--kaleo-locale-chevron-inset) +
            var(--kaleo-locale-chevron-size) +
            var(--kaleo-locale-chevron-gap)
          )
          0 0.625rem;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        appearance: none;
        color: var(--foreground);
        background: var(--background);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 500;
        cursor: pointer;
      }

      .kaleo-locale-selector-chevron {
        position: absolute;
        top: 50%;
        right: var(--kaleo-locale-chevron-inset);
        width: var(--kaleo-locale-chevron-size);
        height: var(--kaleo-locale-chevron-size);
        color: var(--muted-foreground);
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2;
        opacity: 0.5;
        transform: translateY(-50%);
        pointer-events: none;
      }

      .kaleo-locale-selector select:disabled {
        cursor: wait;
        opacity: 0.65;
      }

      .kaleo-locale-selector-error {
        position: absolute;
        z-index: 30;
        top: calc(100% + 0.5rem);
        right: 0;
        width: min(18rem, calc(100vw - 2rem));
        padding: 0.625rem 0.75rem;
        border: 1px solid color-mix(in srgb, var(--destructive) 45%, var(--border));
        border-radius: var(--radius-md);
        color: var(--destructive);
        background: var(--background);
        font-size: 0.75rem;
        line-height: 1.35;
        box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
      }

      .kaleo-header-action {
        display: inline-flex;
        height: 2rem;
        align-items: center;
        justify-content: center;
        padding: 0 0.75rem;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        color: var(--foreground);
        background: var(--background);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        line-height: 1;
        text-decoration: none;
        white-space: nowrap;
        cursor: pointer;
      }

      .kaleo-header-action:hover {
        background: var(--muted);
      }

      .kaleo-header-dashboard {
        border-color: var(--primary);
        color: var(--primary-foreground);
        background: var(--primary);
      }

      .kaleo-header-dashboard:hover {
        color: var(--primary-foreground);
        background: color-mix(in srgb, var(--primary) 88%, black);
      }

      .kaleo-docs-header-actions-compact {
        gap: 0.375rem;
      }

      .kaleo-docs-header-actions-compact .kaleo-locale-selector select {
        max-width: 8.75rem;
      }

      .kaleo-compact-theme-toggle {
        display: inline-flex;
        width: 2rem;
        height: 2rem;
        flex: 0 0 2rem;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        color: var(--foreground);
        background: var(--background);
        cursor: pointer;
      }

      .kaleo-compact-theme-toggle:hover {
        background: var(--muted);
      }

      .kaleo-compact-theme-toggle svg {
        width: 1rem;
        height: 1rem;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2;
      }

      .kaleo-docs-runtime-alert {
        position: relative;
        z-index: 20;
        margin: 0;
        padding: 0.625rem 1rem;
        border-bottom: 1px solid color-mix(in srgb, var(--destructive) 35%, var(--border));
        color: var(--destructive);
        background: color-mix(in srgb, var(--destructive) 8%, var(--background));
        font-size: 0.8125rem;
        font-weight: 500;
        text-align: center;
      }

      .kaleo-locale-selector select:focus-visible,
      .kaleo-header-action:focus-visible,
      .kaleo-compact-theme-toggle:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
      }

      header[data-pagefind-ignore="all"] button[data-kaleo-framework="navigation-toggle"] {
        display: none;
      }

      [data-pagefind-ignore="all"] > nav a[href="${APP_ORIGIN}"] {
        display: none;
      }

      [role="dialog"] a[href="${APP_ORIGIN}"] {
        height: 2.25rem;
        justify-content: center;
        margin-top: 0.75rem;
        padding: 0 0.875rem;
        border: 1px solid var(--primary);
        border-radius: var(--radius-md);
        color: var(--primary-foreground);
        background: var(--primary);
        font-weight: 600;
      }

      [role="dialog"] a[href="${APP_ORIGIN}"]:hover {
        color: var(--primary-foreground);
        background: color-mix(in srgb, var(--primary) 88%, black);
      }

      .kaleo-page-share {
        margin: 1.25rem 0 0;
      }

      .kaleo-page-share-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.625rem;
      }

      .kaleo-page-share-actions button {
        display: inline-flex;
        min-height: 2.5rem;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        color: var(--foreground);
        background: var(--background);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        line-height: 1.25;
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
        cursor: pointer;
      }

      .kaleo-page-share-actions button:hover {
        background: var(--muted);
      }

      .kaleo-page-share-actions button:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
      }

      .kaleo-page-share-actions svg {
        width: 1rem;
        height: 1rem;
        flex: 0 0 1rem;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2;
      }

      .typography .kaleo-doc-image {
        width: 100%;
        margin: 1rem 0 1.5rem;
      }

      .typography .kaleo-doc-image img {
        display: block;
        width: 100%;
        height: auto;
        margin: 0;
        border: 1px solid var(--border);
        border-radius: 0.75rem;
        background: var(--muted);
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
      }

      .typography .kaleo-doc-image figcaption {
        margin: 0.5rem 0.25rem 0;
        color: color-mix(in srgb, var(--foreground) 70%, transparent);
        font-size: 0.8125rem;
        line-height: 1.35;
        text-align: center;
      }

      .typography [role="note"].kaleo-getting-started-callout {
        margin-top: 0.5rem;
        margin-bottom: 2rem;
      }

      .kaleo-page-share-status {
        min-height: 1.25rem;
        margin: 0.5rem 0 0;
        color: var(--muted-foreground);
        font-size: 0.75rem;
      }

      .kaleo-intercom-trigger:focus-visible,
      .kaleo-language-search-clear:focus-visible,
      .kaleo-language-catalog-alert button:focus-visible,
      .kaleo-language-empty button:focus-visible {
        border-radius: var(--radius-sm);
        outline: 2px solid var(--ring);
        outline-offset: 3px;
      }

      .kaleo-language-catalog {
        margin-top: 1.5rem;
      }

      .kaleo-language-search {
        position: relative;
        display: block;
        max-width: 30rem;
        margin-bottom: 1rem;
      }

      .kaleo-language-search > svg {
        position: absolute;
        top: 50%;
        left: 0.875rem;
        width: 1rem;
        height: 1rem;
        color: var(--muted-foreground);
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2;
        transform: translateY(-50%);
        pointer-events: none;
      }

      .kaleo-language-search input {
        box-sizing: border-box;
        width: 100%;
        height: 2.75rem;
        padding: 0 2.75rem 0 2.5rem;
        border: 1px solid var(--input);
        border-radius: var(--radius-md);
        color: var(--foreground);
        background: var(--background);
        font: inherit;
        font-size: 0.875rem;
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
        outline: none;
      }

      .kaleo-language-search input::placeholder {
        color: var(--muted-foreground);
      }

      .kaleo-language-search input:focus {
        border-color: var(--ring);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--ring) 18%, transparent);
      }

      .kaleo-language-search input::-webkit-search-cancel-button {
        appearance: none;
      }

      .kaleo-language-search-clear {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        display: inline-flex;
        width: 2rem;
        height: 2rem;
        padding: 0;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: var(--radius-sm);
        color: var(--primary);
        background: transparent;
        cursor: pointer;
        transform: translateY(-50%);
      }

      .kaleo-language-search-clear:hover {
        background: color-mix(in srgb, var(--primary) 10%, transparent);
      }

      .kaleo-language-search-clear svg {
        width: 1rem;
        height: 1rem;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-width: 2.5;
      }

      .kaleo-language-chips {
        display: flex;
        flex-wrap: wrap;
        column-gap: 0.5rem;
        row-gap: 0.375rem;
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .kaleo-language-chip {
        display: inline-flex;
        min-width: 0;
        min-height: 2.25rem;
        margin: 0;
        align-items: center;
        gap: 0.5rem;
        padding: 0.375rem 0.75rem;
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        color: var(--secondary-foreground);
        background: var(--secondary);
        line-height: 1;
      }

      .kaleo-language-emoji {
        display: inline-flex;
        width: 1.125rem;
        flex: 0 0 1.125rem;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        line-height: 1;
      }

      .kaleo-language-english-name {
        font-size: 0.8125rem;
        font-weight: 400;
        white-space: nowrap;
      }

      .kaleo-language-empty {
        padding: 2rem;
        border: 1px dashed var(--border);
        border-radius: var(--radius-md);
        color: var(--muted-foreground);
        text-align: center;
      }

      .kaleo-language-empty p {
        margin: 0;
        color: var(--foreground);
        font-weight: 500;
      }

      .kaleo-language-empty button {
        margin-top: 0.5rem;
        padding: 0;
        border: 0;
        color: var(--primary);
        background: transparent;
        font: inherit;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
      }

      .kaleo-language-catalog-alert {
        display: flex;
        margin: 0 0 1rem;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem;
        border: 1px solid color-mix(in srgb, var(--destructive) 32%, var(--border));
        border-radius: var(--radius-md);
        color: var(--foreground);
        background: color-mix(in srgb, var(--destructive) 7%, var(--background));
        font-size: 0.8125rem;
        line-height: 1.4;
      }

      .kaleo-language-catalog-alert span {
        flex: 1;
      }

      .kaleo-language-catalog-alert button {
        flex: 0 0 auto;
        padding: 0;
        border: 0;
        color: var(--primary);
        background: transparent;
        font: inherit;
        font-weight: 600;
        cursor: pointer;
      }

      .kaleo-language-table-wrap {
        max-height: 38rem;
        overflow: auto;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        background: var(--background);
      }

      .kaleo-language-table {
        width: 100%;
        margin: 0;
        border-spacing: 0;
        border-collapse: separate;
        font-size: 0.875rem;
      }

      .kaleo-language-table th,
      .kaleo-language-table td {
        padding: 0.625rem 0.75rem;
        border-bottom: 1px solid var(--border);
      }

      .kaleo-language-table thead th {
        position: sticky;
        z-index: 1;
        top: 0;
        color: var(--muted-foreground);
        background: var(--muted);
        font-size: 0.75rem;
        font-weight: 600;
        text-align: left;
      }

      .kaleo-language-table thead th:not(:first-child),
      .kaleo-language-table td {
        width: 9.5rem;
        text-align: center;
      }

      .kaleo-language-table tbody th {
        font-weight: 400;
        text-align: left;
      }

      .kaleo-language-table thead th:nth-child(2),
      .kaleo-language-table td:nth-child(2) {
        width: 7.5rem;
        white-space: nowrap;
      }

      .kaleo-language-table tbody tr:last-child th,
      .kaleo-language-table tbody tr:last-child td {
        border-bottom: 0;
      }

      .kaleo-language-table tbody tr:hover {
        background: color-mix(in srgb, var(--muted) 55%, transparent);
      }

      .kaleo-table-language {
        display: inline-flex;
        align-items: center;
        gap: 0.625rem;
      }

      .kaleo-table-language > span:first-child {
        display: inline-flex;
        width: 1.25rem;
        flex: 0 0 1.25rem;
        justify-content: center;
        font-size: 1rem;
        line-height: 1;
      }

      .kaleo-table-language-names {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.125rem 0.5rem;
      }

      .kaleo-table-language-secondary-name {
        color: var(--muted-foreground);
        font-size: 0.8125rem;
      }

      .kaleo-language-support-check {
        display: inline-flex;
        width: 1.5rem;
        height: 1.5rem;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        color: var(--primary);
        background: color-mix(in srgb, var(--primary) 12%, transparent);
      }

      .kaleo-language-support-check svg {
        width: 0.875rem;
        height: 0.875rem;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2.5;
      }

      @media (max-width: 63.999rem) {
        button[data-kaleo-framework="toc-toggle"] {
          display: none;
        }
      }

      @media (max-width: 42rem) {
        .kaleo-language-table {
          min-width: 34rem;
        }
      }
    `,
  },
};

export default config;
